import React, { useState } from "react";
import { StyledButton, StyledSpinnerWrapper } from "../styles/styledComponents";

import "../styles/snowzoneComponent.css";
import { Trans, useTranslation } from "react-i18next";
import { useMutation } from "react-query";
import styled from "styled-components";
import { CircularProgress } from "@mui/material";
import ApiDataService from "../services/ApiDataService";
import SnowzoneType from "../types/SnowzoneType";
import maxDistanceSe from "../assets/images/max-distance-se.jpg";
import maxDistanceNo from "../assets/images/max-distance-no.jpg";

const maxDistanceImageByCountry: Record<string, string> = {
  se: maxDistanceSe,
  no: maxDistanceNo,
};

export const StyledLeftSpinnerWrapper = styled(StyledSpinnerWrapper)`
  justify-content: flex-start;
  padding-left: 0;
  margin-top: 10px;
  margin-bottom: 10px;
  margin: 0 auto;
`;

type SnowzoneComponentProps = {
  countryCode?: string;
};

const SnowzoneComponent = (props: SnowzoneComponentProps) => {
  const { countryCode } = props;
  const { t } = useTranslation("trans");

  let showSnowMap: boolean;
  switch (countryCode?.toLowerCase()) {
    case "no": {
      showSnowMap = false;
      break;
    }
    default: {
      showSnowMap = true;
      break;
    }
  }

  const maxDistanceImage =
    maxDistanceImageByCountry[countryCode?.toLowerCase() ?? ""] ?? maxDistanceSe;

  /* Fields values */
  const [distanceElevation, setDistanceElevation] = useState<string>("");
  const [distanceSnowzone, setDistanceSnowzone] = useState<string>("");

  const [consoleSnowzone, setConsoleSnowzone] = useState<string>("");
  const [consoleElevation, setConsoleElevation] = useState<string>("");
  const [consoleRoofLength, setConsoleRoofLength] = useState<string>("");

  /* Results from calculate */
  const [distanceResult, setDistanceResult] = useState<SnowzoneType>();
  const [consoleResult, setConsoleResult] = useState<SnowzoneType>();

  const [distanceError, setDistanceError] = useState(false);
  const [consoleError, setConsoleError] = useState(false);


  const onFieldChanged = (value: string, func: (value: string) => any) => {
    let val = value.replaceAll(',', '.');
    func(val);
  }

  /* Calculate max distance */
  const calculateMaxDistance = () => {
    if (distanceElevation === "" || isNaN(+distanceElevation)) {
      setDistanceError(true);
      return;
    }

    if (distanceSnowzone === "" || isNaN(+distanceSnowzone)) {
      setDistanceError(true);
      return;
    }
    setDistanceError(false);
    postCalculateMaxDistance();
  };

  const calculateConsoleDistance = () => {
    if (consoleSnowzone === "" || isNaN(+consoleSnowzone)) {
      setConsoleError(true);
      return;
    }

    if (consoleElevation === "" || isNaN(+consoleElevation)) {
      setConsoleError(true);
      return;
    }

    if (consoleRoofLength === "" || isNaN(+consoleRoofLength)) {
      setConsoleError(true);
      return;
    }

    postCalculateConsoleDistance();
  };

  const { isLoading: isLoadingMaxDistance, mutate: postCalculateMaxDistance } =
    useMutation<any, Error>(
      async () => {
        return await ApiDataService.calculateMaxDistance(
          Number(distanceElevation),
          Number(distanceSnowzone)
        );
      },
      {
        onSuccess: (res) => {
          setDistanceResult(res);
        },
        onError: (err: any) => {
          // console.log(err.response?.data || err);
        },
      }
    );

  const {
    isLoading: isLoadingConsoleDistance,
    mutate: postCalculateConsoleDistance,
  } = useMutation<any, Error>(
    async () => {
      return await ApiDataService.calculateConsoleDistance(
        Number(consoleElevation),
        Number(consoleRoofLength),
        Number(consoleSnowzone)
      );
    },
    {
      onSuccess: (res) => {
        setConsoleResult(res);
      },
      onError: (err: any) => {
        // console.log(err.response?.data || err);
      },
    }
  );

  return (
    <div className="snowzone">
      <h2>{t('snowzone_page.title')}</h2>
      <p style={{ paddingBottom: '20px' }}>{t('snowzone_page.description')}</p>
      <div className="snowzone-inner">
        <div className="snowzone-inner-left">
          <div className="snowzone-inner-box">
            <h4>{t('snowzone_page.maximum_length_of_roof_pitch')}</h4>
            <div className="inputfield">
              <img src={maxDistanceImage} width="250" height="250" alt="max distance" />
            </div>
            <div className="inputfield">
              <label>{t('snowzone_page.roof_gradient') + " (grader)"}</label>
              <input
                required
                id="distanceElevation"
                value={distanceElevation}
                onChange={(e) => onFieldChanged(e.target.value, setDistanceElevation)}
              />
            </div>
            <div className="inputfield">
              <label>{t('snowzone_page.snow_zone')} (kN/m&sup2;)</label>
              < input
                required
                id="distanceSnowzone"
                value={distanceSnowzone}
                onChange={(e) => onFieldChanged(e.target.value, setDistanceSnowzone)}
              />
            </div>
            {distanceError ? (
              <div className="inputfield errortext">
                <span>
                  {t('snowzone_page.error')}
                </span>
              </div>
            ) : (
              ""
            )}
            <StyledButton typeof="button" onClick={calculateMaxDistance}>
              {t('buttons.calculate')}
            </StyledButton>

            {isLoadingMaxDistance ? (
              <StyledLeftSpinnerWrapper>
                <CircularProgress color="inherit" />
                {t("calculating")}
              </StyledLeftSpinnerWrapper>
            ) : (
              <>
                {distanceResult ? (
                  <div className="resultFields">
                    <div className="resultField">
                      <label>{t('snowzone_page.max_distance_saddle_roof')}</label>
                      <input
                        readOnly
                        value={distanceResult.maxDistanceSaddleRoof}
                      />
                    </div>
                    <div className="resultField" style={{ marginLeft: "10px" }}>
                      <label>{t('snowzone_page.max_distance_pentroof')}</label>
                      <input
                        readOnly
                        value={distanceResult.maxDistancePentRoof}
                      />
                    </div>
                  </div>
                ) : (
                  ""
                )}
              </>
            )}
          </div>
          <div className="snowzone-inner-box">
            <h4>{t('snowzone_page.maximum_distance_brackets')}</h4>
            <div className="inputfield">
              <label>{t('snowzone_page.roof_gradient') + " (grader)"}</label>
              <input
                required
                id="consoleElevation"
                value={consoleElevation}
                onChange={(e) => onFieldChanged(e.target.value, setConsoleElevation)}
              />
            </div>
            <div className="inputfield">
              <label>{t('snowzone_page.length_of_roof_drop') + " (m)"}</label>
              <input
                required
                id="consoleRoofLength"
                value={consoleRoofLength}
                onChange={(e) => onFieldChanged(e.target.value, setConsoleRoofLength)}
              />
            </div>
            <div className="inputfield">
              <label>{t('snowzone_page.snow_zone')} (kN/m&sup2;)</label>
              <input
                required
                id="consoleSnowzone"
                value={consoleSnowzone}
                onChange={(e) => onFieldChanged(e.target.value, setConsoleSnowzone)}
              />
            </div>
            {consoleError ? (
              <div className="inputfield errortext">
                <span>
                  {t('snowzone_page.error')}
                </span>
              </div>
            ) : (
              ""
            )}
            <StyledButton typeof="button" onClick={calculateConsoleDistance}>
              {t('buttons.calculate')}
            </StyledButton>

            {isLoadingConsoleDistance ? (
              <StyledLeftSpinnerWrapper>
                <CircularProgress color="inherit" />
                {t("calculating")}
              </StyledLeftSpinnerWrapper>
            ) : (
              <>
                {consoleResult ? (
                  <div className="resultFields">
                    <div className="resultField">
                      <label>{t('snowzone_page.saddle_roof_cc_measurement') + " (mm)"}</label>
                      <input
                        readOnly
                        value={consoleResult.ccDistanceSaddleRoof}
                      />
                    </div>
                    <div className="resultField" style={{ marginLeft: "10px" }}>
                      <label>{t('snowzone_page.pentroof_cc_measurement') + " (mm)"}</label>
                      <input
                        readOnly
                        value={consoleResult.ccDistancePentRoof}
                      />
                    </div>
                  </div>
                ) : (
                  ""
                )}
              </>
            )}
          </div>
        </div>

        {showSnowMap && (
          <div className="snowzone-inner-right">
            <div className="snowzone-inner-box">
              <Trans
                  i18nKey="snowzone_page.map_instruction"
                  t={t}
                  components={{
                    url: <a href="https://gis2.boverket.se/portal/apps/experiencebuilder/experience/?id=dc80c2fd96814c5aac3efd6ff7a8bc69&page=Sn%C3%B6lastzoner" target="_blank" rel="noopener noreferrer"></a>
                  }}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SnowzoneComponent;
